import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface FormNavigationProps {
  currentStep: number;
  totalSteps: number;
  isStepValid: boolean | (() => boolean);
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  submitText?: string;
  className?: string;
}

export const FormNavigation: React.FC<FormNavigationProps> = ({
  currentStep,
  totalSteps,
  isStepValid,
  onNext,
  onPrevious,
  onSubmit,
  isSubmitting = false,
  submitText,
  className = '',
}) => {
  const { t } = useLanguage();
  const [showValidationWarning, setShowValidationWarning] = React.useState(false);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const getIsValid = () => (typeof isStepValid === 'function' ? isStepValid() : isStepValid);

  const handleNextClick = () => {
    if (!getIsValid()) {
      setShowValidationWarning(true);
      setTimeout(() => setShowValidationWarning(false), 3000);
      return;
    }
    setShowValidationWarning(false);
    onNext();
  };

  const handleSubmitClick = () => {
    if (!getIsValid()) {
      setShowValidationWarning(true);
      setTimeout(() => setShowValidationWarning(false), 3000);
      return;
    }
    setShowValidationWarning(false);
    onSubmit();
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {showValidationWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800 shadow-sm">
          {t('form.validation.mandatoryFields')}
        </div>
      )}
      
      <div className="flex items-center justify-between">
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onPrevious}
            className="h-6 px-2 text-xs"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            {t('form.back')}
          </Button>
        )}
        
        <div className="flex items-center space-x-1">
          {Array.from({ length: totalSteps }, (_, index) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full ${
                index === currentStep
                  ? 'bg-black'
                  : index < currentStep
                  ? 'bg-gray-400'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        
        {!isLastStep ? (
          <Button
            type="button"
            size="sm"
            onClick={handleNextClick}
            className="h-6 px-2 text-xs !bg-black !text-white hover:!bg-gray-700 hover:!text-white rounded-lg"
          >
            {t('form.next')}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleSubmitClick}
            disabled={isSubmitting}
            className="h-6 px-2 text-xs !bg-black !text-white hover:!bg-gray-700 hover:!text-white rounded-lg"
          >
            {isSubmitting 
              ? t('form.submitting') 
              : submitText || t('form.submit')
            }
          </Button>
        )}
      </div>
    </div>
  );
};